import { Box, Separator, Flex, HStack, Stack } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useEffect, useMemo } from "react";
import {
  MapComponent,
  MapMode,
  MapPageView,
} from "../../survey/stores/MapStore";
import {
  AppHeader,
  AppHeaderHeight,
} from "../../shared/views/AppHeader/AppHeader";
import { LoadingHeader } from "./views/LoadingHeader";
import { NodeRefreshAlert } from "./views/NodeRefreshAlert";
import { FiltersBar } from "./views/FiltersBar/FiltersBar";
import { MainMap } from "./views/MainMap";
import { DetailBar } from "./views/DetailBar/DetailBar";
import { DashboardView } from "./views/dashboard/DashboardView";
import { ForecastHeader } from "./views/CraForecast/ForecastHeader";
import { Header } from "./views/Header/Header";
import { CodingPage } from "./views/coding/CodingPage";
import { LifeCycleStage } from "../../cra/models/LifeCycleStage";
import { useParams, useSearchParams } from "react-router-dom";
import { useStores } from "../../shared/stores/RootStore";
import { PendingCoordChangesControls } from "./views/overlay/PendingCoordChangesControls";
import { MySurveysList } from "../../survey/views/MySurveyList/MySurveyList";
import { SurveyMapSelectionPopover } from "../home/SurveyMapSelectionPopover";
import { useAutoUpdateQueryParam } from "./hooks/useAutoUpdateQueryParam";
import { FetchingStatus } from "../../survey/stores/LoadingStore";
import { MapControls } from "./views/MapControls";
import { ShortcutList } from "./views/overlay/ShortcutList";
import { NodeList } from "./views/BottomBar/cra-way/NodeList";
import { ToggleVisibilityButton } from "./ToggleVisibilityButton";
import { ViewComments } from "./views/BottomBar/ViewComments";
import { SurveyDefaultsModal } from "../../survey/views/SurveyDefaultsModal/SurveyDefaultsModal";
import { HlaSelectionHeader } from "./views/HlaSelectionHeader";
import { MapLegend } from "./views/overlay/MapLegend";
import { toast } from "../../shared/views/CustomToaster";
import { HlaSimulationPhotoView } from "../../hla/views/StreetView/HlaSimulationPhotoView";

enum ParamNames {
  CraWayId = "craWayId",
  CraNodeId = "craNodeId",
  HlaWayId = "hlaWayId",
  SimulationWayId = "simulationWayId",
}

const MapPageBase: React.FC<{
  isShared?: boolean;
  isHome?: boolean;
}> = ({ isShared = false, isHome = false }) => {
  const {
    mapStore,
    filterStore,
    craWayStore,
    craNodeStore,
    craForecastStore,
    loadingStore,
    surveyStore,
    dashboardStore,
    hlaStore,
    simulationStore,
    mapQueryStore,
  } = useStores();

  const craWays = filterStore.originalOrForecastedCraWays;
  const { surveyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const allNodes = filterStore.originalOrForecastedNodes;
  const selectedCraWay = craWayStore.getSelected();
  const selectedHlaWay = hlaStore.getSelected();
  const selectedSurvey = surveyStore.getSelected();

  useEffect(() => {
    const service = "Lane Patrol";

    if (selectedHlaWay) {
      document.title = `${selectedHlaWay.name} - ${service}`;
    } else if (selectedCraWay) {
      document.title = `${selectedCraWay.name} - ${service}`;
    } else if (selectedSurvey) {
      document.title = `${selectedSurvey.name} - ${service}`;
    } else {
      document.title = `My surveys - ${service}`;
    }
  }, [selectedHlaWay, selectedCraWay, selectedSurvey]);

  const preSelection: Record<ParamNames, string> = useMemo(() => {
    return {
      craWayId: searchParams.get(ParamNames.CraWayId) ?? "",
      craNodeId: searchParams.get(ParamNames.CraNodeId) ?? "",
      hlaWayId: searchParams.get(ParamNames.HlaWayId) ?? "",
      simulationWayId: searchParams.get(ParamNames.SimulationWayId) ?? "",
    };
    // eslint-disable-next-line
  }, [surveyId]);

  useAutoUpdateQueryParam(
    ParamNames.CraWayId,
    craWayStore.selectedId,
    setSearchParams,
  );

  useAutoUpdateQueryParam(
    ParamNames.CraNodeId,
    craNodeStore.selectedId,
    setSearchParams,
  );

  useAutoUpdateQueryParam(
    ParamNames.HlaWayId,
    hlaStore.selectedId,
    setSearchParams,
  );

  useAutoUpdateQueryParam(
    ParamNames.SimulationWayId,
    simulationStore.selectedSimulationWay?.id,
    setSearchParams,
  );

  const onPageLoad = async () => {
    craWayStore.resetState();
    mapStore.resetState();
    filterStore.resetState();
    craNodeStore.resetState();
    hlaStore.resetState();
    simulationStore.resetState();
    craForecastStore.resetState();
    dashboardStore.resetState();
    mapQueryStore.resetState();

    mapStore.refreshMapWindow();

    let scope = "Home";

    if (isHome) {
      mapStore.setMode(MapMode.HomePage);
      mapStore.recenterMap();
      surveyStore.select(undefined);

      loadingStore.registerScope(scope);
      await loadingStore.loadSurveysList(scope);
    } else {
      mapStore.setMode(MapMode.SurveyPage);
      scope = surveyId!;

      loadingStore.setFetchingStatus({
        survey: FetchingStatus.NotStarted,
        craWays: FetchingStatus.NotStarted,
        hlaWays: FetchingStatus.NotStarted,
        dashboards: FetchingStatus.NotStarted,
        hlaForecastScenarios: FetchingStatus.NotStarted,
        craForecastScenarios: FetchingStatus.NotStarted,
        nodesAndMetadata: FetchingStatus.NotStarted,
        surveyDefaults: FetchingStatus.NotStarted,
      });

      loadingStore.registerScope(scope);

      if (isShared) {
        await loadingStore.loadSurvey(surveyId!, scope);
      } else {
        await loadingStore.loadSurveysList(scope);
        surveyStore.select(surveyId!);

        if (surveyStore.getSelected() == null) {
          await loadingStore.loadSurvey(surveyId!, scope).catch((e) => {
            if (e.response?.status === 403) {
              toast("error", "You don't have access to this project.");
            } else {
              toast("error", "Failed to load project. Try again later.");
            }
            throw e;
          });
        }
      }

      await loadingStore.loadSurveyDefaults(surveyId!, scope);

      if (!loadingStore.isScopeActive(scope)) return;

      mapStore.recenterMap();

      if (!loadingStore.isScopeActive(scope)) return;
      await loadingStore.loadCraWays(surveyId!, scope);

      if (preSelection[ParamNames.CraWayId]) {
        craWayStore.select(preSelection[ParamNames.CraWayId]);
      }

      if (!loadingStore.isScopeActive(scope)) return;
      await loadingStore.loadCraNodesAndMetadata(scope);

      if (preSelection[ParamNames.CraNodeId]) {
        craNodeStore.select(preSelection[ParamNames.CraNodeId]);
      }

      if (!loadingStore.isScopeActive(scope)) return;
      await loadingStore.loadHlaWays(surveyId!, scope);

      if (preSelection[ParamNames.HlaWayId]) {
        hlaStore.select(preSelection[ParamNames.HlaWayId]);
      }

      if (preSelection[ParamNames.SimulationWayId]) {
        simulationStore.selectSimulationWayId(
          preSelection[ParamNames.SimulationWayId],
        );
      }

      mapStore.recenterMap();

      await loadingStore.loadCraForecastScenarios(surveyId!, scope);
      await loadingStore.loadHlaForecastScenarios(surveyId!, scope);

      if (!loadingStore.isScopeActive(scope)) return;
      await loadingStore.loadDashboards(surveyId!, scope);
    }

    mapStore.recenterMap();
  };

  useEffect(() => {
    onPageLoad();

    // eslint-disable-next-line
  }, [surveyId]);

  useEffect(() => {
    filterStore.filterCraWays();

    // eslint-disable-next-line
  }, [craWays]);

  useEffect(() => {
    filterStore.filterNodes();

    // eslint-disable-next-line
  }, [allNodes]);
  return (
    <Flex
      flexDir="column"
      maxHeight="100vh"
      maxWidth="100vw"
      flexGrow={1}
      overflow="auto"
    >
      <AppHeader>
        <Header isHome={isHome} />
      </AppHeader>

      <AppHeaderHeight />

      <NodeRefreshAlert />
      <ForecastHeader />

      <HStack
        align="stretch"
        gap={0}
        flexGrow={1}
        overflow="auto"
        separator={<Separator />}
      >
        {!isHome && mapStore.view === MapPageView.Dashboard && (
          <Stack flexGrow={1} overflow="auto" gap={0}>
            <DashboardView />
          </Stack>
        )}

        {isHome && <MySurveysList />}

        {!isHome &&
          (mapStore.view === MapPageView.Map ||
            mapStore.view === MapPageView.SimulationsCompare) && (
            <>
              {mapStore.componentVisibility.detail && <DetailBar />}

              {mapStore.view === MapPageView.Map && (
                <ToggleVisibilityButton
                  component={MapComponent.Detail}
                  arrowAngleWhenOpen={180}
                />
              )}
            </>
          )}

        {mapStore.view === MapPageView.Map && (
          <Stack flexGrow={1} overflow="auto" gap={0}>
            {selectedCraWay?.lifeCycleStage === LifeCycleStage.Coding &&
            loadingStore.fetchingState.nodesAndMetadata ===
              FetchingStatus.Done ? (
              <CodingPage />
            ) : (
              <Stack
                flexGrow={1}
                alignItems="stretch"
                gap={0}
                separator={<Separator />}
              >
                <HlaSelectionHeader />

                <Box flexGrow={1} position="relative">
                  <MainMap isHome={isHome} />
                  <MapControls />

                  {!isHome && <MapLegend />}
                  {isHome && <SurveyMapSelectionPopover />}

                  <Stack
                    bottom={16}
                    left={4}
                    position="absolute"
                    zIndex={1}
                    alignItems="start"
                  >
                    {!isHome && <ShortcutList />}
                  </Stack>

                  <PendingCoordChangesControls />
                </Box>

                <HlaSimulationPhotoView />
              </Stack>
            )}

            {selectedCraWay?.lifeCycleStage === LifeCycleStage.DataCollection &&
              !!craNodeStore.nodesByWayId.get(selectedCraWay.id!)?.length && (
                <>
                  <Separator />
                  <NodeList />
                </>
              )}

            {selectedCraWay?.lifeCycleStage === LifeCycleStage.Review && (
              <>
                <Separator />
                <ViewComments />
              </>
            )}
          </Stack>
        )}

        {!isHome &&
          !mapQueryStore.active &&
          mapStore.view !== MapPageView.SimulationsCompare && (
            <Stack direction="row" gap={0}>
              <ToggleVisibilityButton
                component={MapComponent.Filters}
                arrowAngleWhenOpen={0}
              />

              {mapStore.componentVisibility.filters && <FiltersBar />}
            </Stack>
          )}
      </HStack>

      <LoadingHeader />

      {surveyStore.showSurveyDefaultsModal && <SurveyDefaultsModal />}
    </Flex>
  );
};

export const MapPage = observer(MapPageBase);
